import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { computed, DestroyRef, effect, inject, Injector, Signal, untracked } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

export interface IRouterState {
  route: {
    current: string;
    last: string | null;
    navigationHistory: string[];
    hasPreviousRoutes: boolean;
  };
  backNavigationPath: string | null;
  backNavigationExtras: NavigationExtras;
  currentRouteId: string;
  url: string;
  lastChildRouteData?: any;
}

export const initialRouterStore: IRouterState = {
  route: {
    current: '',
    last: null,
    navigationHistory: [],
    hasPreviousRoutes: false,
  },
  backNavigationPath: null,
  backNavigationExtras: {},
  currentRouteId: '',
  url: null,
  lastChildRouteData: {
    title: '',
    subtitle: '',
  },
};

// Função utilitária para obter a rota filha mais profunda
export const getLastChildRoute = (route: ActivatedRoute | null | undefined): ActivatedRoute | null => {
  if (!route) return null;
  let last: ActivatedRoute = route;
  while (last.firstChild) {
    last = last.firstChild;
  }
  return last ?? null;
};

// Função para extrair dados da rota filha mais profunda
const extractLastChildRouteData = (route: ActivatedRoute | null | undefined) => {
  const lastChild = getLastChildRoute(route);
  return lastChild?.snapshot?.data ?? null;
};

const setupSignalEffects = (navigate: () => void, signalSource: Signal<boolean>[], injector: Injector) => {
  signalSource.forEach(signal => {
    effect(() => {
      if (signal()) {
        untracked(() => navigate());
      }
    }, { injector });
  });
};

export const RouterStore = signalStore(
  { providedIn: 'root' },
  withState(initialRouterStore),
  withComputed((
    state,
    activatedRoute = inject(ActivatedRoute),
    currentRoute = toSignal(activatedRoute.params),

    ) => {
    return {
      getCurrentRoute: computed(() => state.url()),
      getCurrentRouteParams: computed(() => currentRoute()),
      getCurrentRouteQueryParams: computed(() => toSignal(activatedRoute.queryParams)),
      getLastChildRouteData: computed(() => state.lastChildRouteData()),
    }
  }),
  withMethods((
    state,
    router = inject(Router),
    injector = inject(Injector),
    activatedRoute = inject(ActivatedRoute)
  ) => {
    const goBack = () => {
      if (state?.backNavigationPath()) {
        router.navigate([state.backNavigationPath()], state.backNavigationExtras()).then();
      }
    };

    const methods = {
      /**
       * `extras` carries what the path alone cannot — the anchor of the section
       * that opened the current screen, so `goBack` returns to it and not to the
       * top of the destination page.
       */
      setBackNavigationPath: (path: string, extras: NavigationExtras = {}) => {
        patchState(state, {backNavigationPath: path, backNavigationExtras: extras});
      },
      navigate: (path: string[], extras?: NavigationExtras) => {
        return router.navigate(path, extras).then();
      },
      navigateOnSignal: (path: string[], signal: Signal<boolean>[]) => {
        setupSignalEffects(() => router.navigate(path).then(), signal, injector);
      },
      goBack,
      goBackOnSignal: (signal: Signal<boolean>[]) => {
        setupSignalEffects(goBack, signal, injector);
      },
      clearBackNavigation: () => {
        patchState(state, {backNavigationPath: null, backNavigationExtras: {}});
      },
      setRouteId: (currentRouteId: string) => {
        patchState(state, { currentRouteId });
      },
      clearRouteId: () => {
        patchState(state, { currentRouteId: '' });
      },
      updateLastChildRouteData: (route?: ActivatedRoute) => {
        const routeData = extractLastChildRouteData(route ?? router.routerState.root);
        patchState(state, { lastChildRouteData: routeData });
      }
    };

    return methods;
  }),
  withHooks((
    state,
    router = inject(Router),
    destroyRef = inject(DestroyRef),
    activatedRoute = inject(ActivatedRoute)
  ) => ({
    onInit: () => {
      const updateUrl = () => {
        patchState(state, { url: router.routerState.snapshot.url });
      };

      const updateRouteData = () => {
        const routeData = extractLastChildRouteData(router.routerState.root ?? activatedRoute);
        patchState(state, { lastChildRouteData: routeData });
      };

      // Configuração do efeito para atualizar os dados em cada alteração de rota
      // Isso é executado para todas as rotas, não apenas para um componente específico
      // Atualização inicial ao carregar
      updateUrl();
      updateRouteData();

      // Atualização em cada navegação
      router.events
        .pipe(
          takeUntilDestroyed(destroyRef),
          filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          updateUrl();
          updateRouteData();
        });

      }
    })
  )
);
